import { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import DebtCard from '../components/DebtCard';
import DebtModal from '../components/DebtModal';
import PaymentModal from '../components/PaymentModal';
import { getAllDebts, getStats } from '../services/debtService';
import type { Debt } from '../types/debt';
import { Plus, Wallet, AlertTriangle, ArrowRight, Building, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const StatCard = ({ label, value, sub, icon: Icon, accent }: { label: string; value: string; sub?: string; icon: any; accent: string }) => (
  <div className="glass-card rounded-2xl p-5 fade-in-up">
    <div className="flex items-start justify-between mb-3">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${accent}`}>
        <Icon className="h-4 w-4" />
      </div>
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </div>
);

const Home = () => {
  const navigate = useNavigate();
  const [debts, setDebts] = useState<Debt[]>([]);
  const [stats, setStats] = useState(getStats());
  const [modalOpen, setModalOpen] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [editingDebt, setEditingDebt] = useState<Debt | null>(null);
  const [paymentDebt, setPaymentDebt] = useState<Debt | null>(null);

  const refresh = useCallback(() => {
    setDebts(getAllDebts());
    setStats(getStats());
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const recentDebts = [...debts]
    .filter((d) => d.status !== 'paid')
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 4);

  const fmt = (n: number, currency = 'USD') =>
    `${currency} ${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const userEmail = localStorage.getItem('user_email') || 'User';
  const firstName = userEmail.split('@')[0].split('.')[0];

  return (
    <div className="min-h-screen flex" style={{ background: '#0f0f13' }}>
      <Sidebar />
      <main className="flex-1 min-w-0 overflow-y-auto">
        <div className="p-6 max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8 fade-in-up">
            <div>
              <h1 className="text-2xl font-bold text-white">Good day, <span className="gradient-text capitalize">{firstName}</span> 👋</h1>
              <p className="text-sm text-gray-500 mt-1">Here's your personal debt overview</p>
            </div>
            <button
              onClick={() => { setEditingDebt(null); setModalOpen(true); }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 transition-colors shadow-lg shadow-indigo-500/25"
            >
              <Plus className="h-4 w-4" /> Add Debt
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard
              label="Total Debt"
              value={fmt(stats.totalDebt)}
              sub="Across all pending accounts"
              icon={Building}
              accent="bg-rose-500/15 text-rose-400"
            />
            <StatCard
              label="Paid This Month"
              value={fmt(stats.paidThisMonth)}
              sub="Payments made this month"
              icon={CheckCircle2}
              accent="bg-emerald-500/15 text-emerald-400"
            />
            <StatCard
              label="Active Accounts"
              value={String(stats.pendingCount)}
              sub="Pending / partial status"
              icon={Wallet}
              accent="bg-indigo-500/15 text-indigo-400"
            />
            <StatCard
              label="Overdue"
              value={String(stats.overdueCount)}
              sub={stats.overdueCount > 0 ? 'Needs attention' : 'All on track'}
              icon={AlertTriangle}
              accent={stats.overdueCount > 0 ? 'bg-amber-500/15 text-amber-400' : 'bg-green-500/15 text-green-400'}
            />
          </div>

          {/* Recent debts */}
          <div className="fade-in-up delay-300">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-white">Recent Debts</h2>
              <button onClick={() => navigate('/my-debts')} className="flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 transition-colors">
                View all <ArrowRight className="h-3 w-3" />
              </button>
            </div>

            {recentDebts.length === 0 ? (
              <div className="glass-card rounded-2xl p-12 text-center">
                <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center mx-auto mb-4">
                  <Wallet className="h-7 w-7 text-indigo-400" />
                </div>
                <p className="text-white font-medium mb-1">No active debts</p>
                <p className="text-sm text-gray-500">Click "Add Debt" to log personal loans, credit cards, or BNPL.</p>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {recentDebts.map((d) => (
                  <DebtCard
                    key={d.id}
                    debt={d}
                    onEdit={(debt) => { setEditingDebt(debt); setModalOpen(true); }}
                    onPayment={(debt) => { setPaymentDebt(debt); setPaymentModal(true); }}
                    onDeleted={refresh}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      <DebtModal isOpen={modalOpen} onClose={() => setModalOpen(false)} onSaved={refresh} editingDebt={editingDebt} />
      <PaymentModal isOpen={paymentModal} onClose={() => setPaymentModal(false)} onSaved={refresh} debt={paymentDebt} />
    </div>
  );
};

export default Home;
